<template>
  <v-dialog :model-value="modelValue" max-width="1100" scrollable persistent @update:model-value="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-flag-outline</v-icon>
        {{ isNew ? t('admConfig.statusEditor.newStatus') : t('admConfig.statusEditor.editStatus') }}
        <v-chip v-if="draft" size="small" label class="ml-3">id {{ draft.id }}</v-chip>
      </v-card-title>

      <v-card-text style="max-height: 72vh;">
        <v-alert v-if="errors.length" type="error" variant="tonal" density="compact" class="mb-3">
          <ul class="ml-4">
            <li v-for="(e, i) in errors" :key="i" class="text-body-2">{{ e }}</li>
          </ul>
        </v-alert>

        <template v-if="draft">
          <v-tabs v-model="activeTab" density="compact" color="primary" class="mb-4">
            <v-tab value="base">{{ t('admConfig.statusEditor.tabBase') }}</v-tab>
            <v-tab value="workflow">{{ t('admConfig.statusEditor.tabWorkflow') }}</v-tab>
            <v-tab value="followup">{{ t('admConfig.statusEditor.tabFollowUp') }}</v-tab>
            <v-tab value="verifications">
              {{ t('admConfig.statusEditor.tabVerifications') }}
              <v-chip v-if="verifications.length" size="x-small" label class="ml-2">{{ verifications.length }}</v-chip>
            </v-tab>
            <v-tab value="instructions">{{ t('admConfig.statusEditor.tabInstructions') }}</v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- ---------- Base ---------- -->
            <v-window-item value="base">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="draft.descr"
                    :label="t('admConfig.statusEditor.status.descr')"
                    density="compact"
                    counter="60"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :model-value="draft.id"
                    :label="t('admConfig.statusEditor.status.id')"
                    :hint="isNew ? t('admConfig.statusEditor.status.idHintNew') : t('admConfig.statusEditor.status.idHint')"
                    type="number"
                    density="compact"
                    persistent-hint
                    :readonly="!isNew"
                    @update:model-value="v => draft.id = v === '' ? '' : Number(v)"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="draft.group"
                    :items="groupItems"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.statusEditor.status.group')"
                    :hint="t('admConfig.statusEditor.status.groupHint')"
                    density="compact"
                    persistent-hint
                    clearable
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <div class="d-flex align-center" style="gap: 8px;">
                    <v-menu :close-on-content-click="false">
                      <template v-slot:activator="{ props }">
                        <span class="color-swatch" :style="{ background: draft.color || '#BDBDBD' }" v-bind="props"></span>
                      </template>
                      <v-color-picker
                        :model-value="draft.color || '#BDBDBD'"
                        mode="hex"
                        hide-inputs
                        @update:model-value="v => draft.color = normalizeHex(v)"
                      />
                    </v-menu>
                    <v-text-field
                      v-model="draft.color"
                      :label="t('admConfig.statusEditor.status.color')"
                      placeholder="#RRGGBB"
                      density="compact"
                      hide-details
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="draft.icon"
                    :label="t('admConfig.statusEditor.status.icon')"
                    :hint="t('admConfig.statusEditor.status.iconHint')"
                    density="compact"
                    persistent-hint
                    style="max-width: 160px;"
                  />
                  <div class="d-flex align-center flex-wrap mt-2" style="gap: 4px;">
                    <v-btn
                      v-for="emoji in EMOJI_QUICK_PICK"
                      :key="emoji"
                      size="x-small"
                      variant="text"
                      @click="draft.icon = emoji"
                    >
                      {{ emoji }}
                    </v-btn>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-switch
                    v-model="draft.isOptional"
                    :label="t('admConfig.statusEditor.status.isOptional')"
                    :hint="t('admConfig.statusEditor.status.isOptionalHint')"
                    color="primary"
                    density="compact"
                    persistent-hint
                    hide-details="auto"
                  />
                  <v-switch
                    v-model="draft.canArchive"
                    :label="t('admConfig.statusEditor.status.canArchive')"
                    :hint="t('admConfig.statusEditor.status.canArchiveHint')"
                    color="primary"
                    density="compact"
                    persistent-hint
                    hide-details="auto"
                    class="mt-2"
                  />
                  <v-switch
                    v-model="draft.exclusive"
                    :label="t('admConfig.statusEditor.status.exclusive')"
                    :hint="t('admConfig.statusEditor.status.exclusiveHint')"
                    color="primary"
                    density="compact"
                    persistent-hint
                    hide-details="auto"
                    class="mt-2"
                  />
                </v-col>
              </v-row>
            </v-window-item>

            <!-- ---------- Workflow ---------- -->
            <v-window-item value="workflow">
              <v-select
                v-model="statusRoles"
                :items="roleItems"
                item-title="title"
                item-value="value"
                :label="t('admConfig.statusEditor.status.roles')"
                :hint="statusRoles.length
                  ? t('admConfig.statusEditor.status.rolesHint')
                  : t('admConfig.statusEditor.status.rolesHintAll')"
                density="compact"
                persistent-hint
                multiple
                chips
                closable-chips
                :disabled="!roleItems.length"
                :messages="roleItems.length ? undefined : t('admConfig.statusEditor.status.rolesEmpty')"
                class="mb-4"
              />

              <v-select
                v-model="nextStates"
                :items="otherStatusItems"
                item-title="title"
                item-value="value"
                :label="t('admConfig.statusEditor.status.nextStates')"
                :hint="t('admConfig.statusEditor.status.nextStatesHint')"
                density="compact"
                persistent-hint
                multiple
                chips
                closable-chips
              />

              <div v-if="danglingNext.length" class="mt-2">
                <v-chip
                  v-for="id in danglingNext"
                  :key="id"
                  size="x-small"
                  label
                  color="error"
                  variant="tonal"
                  class="mr-1"
                  closable
                  @click:close="nextStates = nextStates.filter(n => Number(n) !== Number(id))"
                >
                  {{ t('admConfig.statusEditor.status.danglingNext', { id }) }}
                </v-chip>
              </div>

              <div class="mt-4">
                <div class="text-caption text-medium-emphasis mb-1">
                  {{ t('admConfig.statusEditor.status.reachableFrom') }}
                </div>
                <div v-if="reachableFrom.length" class="d-flex flex-wrap" style="gap: 4px;">
                  <v-chip v-for="s in reachableFrom" :key="s.id" size="x-small" label variant="tonal">
                    {{ s.icon }} {{ s.descr }} ({{ s.id }})
                  </v-chip>
                </div>
                <v-alert v-else type="warning" variant="tonal" density="compact">
                  {{ t('admConfig.statusEditor.warnUnreachable') }}
                </v-alert>
              </div>

              <v-divider class="my-4" />

              <div class="text-subtitle-2 mb-2">{{ t('admConfig.statusEditor.status.conditionalTitle') }}</div>
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="conditionalMode"
                    :items="conditionalModeItems"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.statusEditor.status.conditionalMode')"
                    density="compact"
                  />
                </v-col>
                <v-col v-if="conditionalMode !== 'none'" cols="12" md="4">
                  <v-combobox
                    v-model="conditional.field"
                    :items="fieldNameItems"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    :label="t('admConfig.statusEditor.status.conditionalField')"
                    density="compact"
                  />
                </v-col>
                <v-col v-if="conditionalMode === 'value' || conditionalMode === 'notValue'" cols="12" md="4">
                  <v-combobox
                    v-model="conditional.value"
                    :items="conditionalValueSuggestions"
                    :label="t('admConfig.statusEditor.status.conditionalValue')"
                    density="compact"
                  />
                </v-col>
                <v-col v-if="conditionalMode === 'values'" cols="12" md="4">
                  <v-combobox
                    v-model="conditional.values"
                    :items="conditionalValueSuggestions"
                    :label="t('admConfig.statusEditor.status.conditionalValues')"
                    density="compact"
                    multiple
                    chips
                    closable-chips
                  />
                </v-col>
              </v-row>
            </v-window-item>

            <!-- ---------- Follow-up e scadenze ---------- -->
            <v-window-item value="followup">
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-text-field
                    :model-value="draft.followUpDays ?? null"
                    :label="t('admConfig.statusEditor.status.followUpDays')"
                    :hint="t('admConfig.statusEditor.status.followUpDaysHint')"
                    type="number"
                    density="compact"
                    persistent-hint
                    clearable
                    @update:model-value="v => draft.followUpDays = v === '' || v === null ? undefined : Number(v)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-combobox
                    v-model="draft.followUpCapField"
                    :items="fieldNameItems"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    :label="t('admConfig.statusEditor.status.followUpCapField')"
                    :hint="t('admConfig.statusEditor.status.followUpCapFieldHint')"
                    density="compact"
                    persistent-hint
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-combobox
                    v-model="draft.followUpCapReleaseField"
                    :items="fieldNameItems"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    :label="t('admConfig.statusEditor.status.followUpCapReleaseField')"
                    density="compact"
                    clearable
                  />
                </v-col>
              </v-row>

              <div class="d-flex align-center mt-4 mb-2" style="gap: 12px;">
                <span class="text-subtitle-2">{{ t('admConfig.statusEditor.status.followUpRules') }}</span>
                <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" @click="addFollowUpRule">
                  {{ t('admConfig.statusEditor.add') }}
                </v-btn>
              </div>
              <div class="text-caption text-medium-emphasis mb-2">
                {{ t('admConfig.statusEditor.status.followUpRulesHint') }}
              </div>

              <v-row v-for="(rule, index) in followUpRules" :key="index" dense class="align-center">
                <v-col cols="12" md="4">
                  <v-combobox
                    v-model="rule.field"
                    :items="fieldNameItems"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    :label="t('admConfig.statusEditor.status.conditionalField')"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="rule.value"
                    :label="t('admConfig.statusEditor.status.conditionalValue')"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="8" md="3">
                  <v-text-field
                    v-model.number="rule.days"
                    :label="t('admConfig.statusEditor.status.followUpDays')"
                    type="number"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="4" md="1" class="text-right">
                  <v-btn icon variant="text" size="small" color="error" @click="followUpRules.splice(index, 1)">
                    <v-icon size="small">mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <div class="d-flex align-center mb-2" style="gap: 12px;">
                <span class="text-subtitle-2">{{ t('admConfig.statusEditor.deadlines.title') }}</span>
                <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" @click="addDeadline">
                  {{ t('admConfig.statusEditor.add') }}
                </v-btn>
              </div>

              <v-alert v-if="!deadlines.length" type="info" variant="tonal" density="compact">
                {{ t('admConfig.statusEditor.deadlines.empty') }}
              </v-alert>

              <v-card v-for="(deadline, index) in deadlines" :key="index" variant="outlined" class="mb-2">
                <v-card-text class="pb-2">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-combobox
                        v-model="deadline.field"
                        :items="dateFieldItems"
                        item-title="title"
                        item-value="value"
                        :return-object="false"
                        :label="t('admConfig.statusEditor.deadlines.field')"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-combobox
                        v-model="deadline.doneField"
                        :items="dateFieldItems"
                        item-title="title"
                        item-value="value"
                        :return-object="false"
                        :label="t('admConfig.statusEditor.deadlines.doneField')"
                        :hint="t('admConfig.statusEditor.deadlines.doneFieldHint')"
                        density="compact"
                        persistent-hint
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="deadline.label"
                        :label="t('admConfig.statusEditor.deadlines.label')"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        v-model.number="deadline.offsetDays"
                        :label="t('admConfig.statusEditor.deadlines.offsetDays')"
                        :hint="t('admConfig.statusEditor.deadlines.offsetDaysHint')"
                        type="number"
                        density="compact"
                        persistent-hint
                        clearable
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        v-model.number="deadline.warnHours"
                        :label="t('admConfig.statusEditor.deadlines.warnHours')"
                        type="number"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field
                        v-model.number="deadline.criticalHours"
                        :label="t('admConfig.statusEditor.deadlines.criticalHours')"
                        :error="deadline.criticalHours > deadline.warnHours"
                        :error-messages="deadline.criticalHours > deadline.warnHours
                          ? t('admConfig.statusEditor.deadlines.errCritical') : ''"
                        type="number"
                        density="compact"
                      />
                    </v-col>
                    <v-col cols="6" md="3" class="d-flex align-center justify-space-between">
                      <v-switch
                        v-model="deadline.alertOnly"
                        :label="t('admConfig.statusEditor.deadlines.alertOnly')"
                        color="primary"
                        density="compact"
                        hide-details
                      />
                      <v-btn icon variant="text" size="small" color="error" @click="deadlines.splice(index, 1)">
                        <v-icon size="small">mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- ---------- Azioni IA ---------- -->
            <!-- Lo stato NON configura le azioni: le sceglie dalla biblioteca (Impostazioni ->
                 Editor delle azioni IA) e decide quali sono accese qui. Così la stessa azione si
                 riusa in più stati senza riconfigurarla. -->
            <v-window-item value="verifications">
              <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
                <span class="text-subtitle-2">{{ t('admConfig.statusEditor.verifications.title') }}</span>
                <v-select
                  v-model="actionToAdd"
                  :items="addableActionItems"
                  item-title="title"
                  item-value="value"
                  :label="t('admConfig.statusEditor.verifications.addAction')"
                  :no-data-text="t('admConfig.statusEditor.verifications.noAction')"
                  density="compact"
                  hide-details
                  style="max-width: 400px;"
                />
                <v-btn
                  size="small"
                  variant="tonal"
                  prepend-icon="mdi-plus"
                  :disabled="!actionToAdd"
                  @click="addVerification"
                >
                  {{ t('admConfig.statusEditor.add') }}
                </v-btn>
              </div>
              <div class="text-caption text-medium-emphasis mb-3">
                {{ t('admConfig.statusEditor.verifications.refHint') }}
              </div>

              <v-alert v-if="!verifications.length" type="info" variant="tonal" density="compact">
                {{ t('admConfig.statusEditor.verifications.empty') }}
              </v-alert>

              <v-card
                v-for="(verification, index) in verifications"
                :key="index"
                variant="outlined"
                class="mb-2"
                :class="{ 'verification--disabled': verification.enabled === false }"
              >
                <v-card-text class="py-2">
                  <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                    <!-- Interruttore acceso/spento: un'azione spenta non compare nel form e non è
                         eseguibile nemmeno chiamando l'endpoint per id. -->
                    <v-switch
                      v-model="verification.enabled"
                      :label="verification.enabled === false
                        ? t('admConfig.statusEditor.verifications.disabled')
                        : t('admConfig.statusEditor.verifications.enabled')"
                      color="primary"
                      density="compact"
                      hide-details
                      class="flex-grow-0"
                    />

                    <v-icon>{{ actionFor(verification)?.icon || 'mdi-robot-outline' }}</v-icon>
                    <span class="text-subtitle-2">
                      {{ actionFor(verification)?.label || verification.ref }}
                    </span>
                    <code class="text-caption">{{ verification.id || verification.ref }}</code>

                    <v-chip v-if="actionFor(verification)" size="x-small" label variant="tonal">
                      {{ actionFor(verification).engine === 'tool'
                        ? t('admConfig.statusEditor.verifications.engineTool')
                        : t('admConfig.statusEditor.verifications.engineBackend') }}
                    </v-chip>
                    <v-chip
                      v-if="actionFor(verification)?.steps?.length"
                      size="x-small" label variant="tonal" color="primary"
                    >
                      {{ t('admConfig.aiActions.stepCount', { count: actionFor(verification).steps.length }) }}
                    </v-chip>
                    <v-chip
                      v-else-if="actionFor(verification)?.skill"
                      size="x-small" label variant="tonal"
                    >
                      {{ actionFor(verification).skill }}
                    </v-chip>
                    <!-- Spegnere un'azione legata a un campo di validazione lascia il gate attivo:
                         il campo resta obbligatorio ma nessuno può più valorizzarlo dall'interfaccia. -->
                    <v-chip
                      v-if="verification.enabled === false && actionFor(verification)?.validationField"
                      size="x-small" label color="warning" variant="tonal"
                    >
                      <v-icon start size="x-small">mdi-alert-outline</v-icon>
                      {{ t('admConfig.statusEditor.verifications.disabledGateWarning', {
                        field: actionFor(verification).validationField }) }}
                    </v-chip>
                    <v-chip
                      v-else-if="actionFor(verification)?.validationField"
                      size="x-small" label variant="tonal" color="success"
                    >
                      <v-icon start size="x-small">mdi-check-decagram-outline</v-icon>
                      {{ actionFor(verification).validationField }}
                    </v-chip>

                    <v-spacer />

                    <v-btn icon variant="text" size="x-small" :disabled="index === 0" @click="moveVerification(index, -1)">
                      <v-icon size="small">mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon variant="text" size="x-small"
                      :disabled="index === verifications.length - 1"
                      @click="moveVerification(index, 1)"
                    >
                      <v-icon size="small">mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon variant="text" size="x-small" color="error" @click="removeVerification(index)">
                      <v-icon size="small">mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <!-- Id runtime diverso dal nome dell'azione: eredità di un cfg.json in cui questo
                       stato aveva una configurazione sua. Lo storico delle esecuzioni sta su questo id. -->
                  <div
                    v-if="verification.id && verification.id !== verification.ref"
                    class="text-caption text-medium-emphasis mt-1"
                  >
                    {{ t('admConfig.statusEditor.verifications.legacyId', {
                      id: verification.id, ref: verification.ref }) }}
                  </div>

                  <v-alert
                    v-if="!actionFor(verification)"
                    type="error" variant="tonal" density="compact" class="mt-2"
                  >
                    {{ t('admConfig.statusEditor.verifications.errMissingAction', { ref: verification.ref }) }}
                  </v-alert>

                  <!-- Prerequisiti del pulsante in QUESTA fase. Spento: valgono quelli della
                       biblioteca (mostrati qui accanto, in sola lettura). -->
                  <v-divider class="my-2" />
                  <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                    <v-switch
                      v-model="verification.ownConditions"
                      :label="t('admConfig.conditions.own')"
                      color="primary"
                      density="compact"
                      hide-details
                      class="flex-grow-0"
                    />
                    <span v-if="!verification.ownConditions" class="text-caption text-medium-emphasis">
                      {{ inheritedConditionsLabel(verification) }}
                    </span>
                  </div>
                  <ConditionsEditor
                    v-if="verification.ownConditions"
                    v-model="verification.conditionalOn"
                    :fields="fields"
                    :hint="t('admConfig.conditions.statusHint')"
                    class="mt-2"
                  />
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- ---------- Istruzioni ---------- -->
            <v-window-item value="instructions">
              <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                <v-text-field
                  v-model="draft.instructionKey"
                  :label="t('admConfig.statusEditor.instructions.key')"
                  density="compact"
                  hide-details
                  style="max-width: 260px;"
                />
                <v-chip v-if="keyIsNonCanonical" size="small" label color="warning" variant="tonal">
                  {{ t('admConfig.statusEditor.instructions.keyNonCanonical', { key: canonicalKey }) }}
                </v-chip>
                <v-btn v-if="keyIsNonCanonical" size="x-small" variant="tonal" @click="draft.instructionKey = canonicalKey">
                  {{ t('admConfig.statusEditor.instructions.normalizeKey') }}
                </v-btn>
                <v-chip v-if="!html.trim()" size="small" label color="grey" variant="tonal">
                  {{ t('admConfig.statusEditor.instructions.keyMissing') }}
                </v-chip>
              </div>

              <div class="d-flex align-center flex-wrap mb-2" style="gap: 4px;">
                <v-btn size="x-small" variant="text" @click="insertSnippet('<p></p>')">&lt;p&gt;</v-btn>
                <v-btn size="x-small" variant="text" @click="insertSnippet('<strong></strong>')">&lt;strong&gt;</v-btn>
                <v-btn size="x-small" variant="text" @click="insertSnippet('<ol>\n  <li></li>\n</ol>')">&lt;ol&gt;</v-btn>
                <v-btn size="x-small" variant="text" @click="insertSnippet('<ul>\n  <li></li>\n</ul>')">&lt;ul&gt;</v-btn>
                <v-btn size="x-small" variant="text" @click="insertSnippet('<p class=&quot;tip&quot;></p>')">tip</v-btn>
              </div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="html"
                    :label="t('admConfig.statusEditor.instructions.source')"
                    rows="16"
                    density="compact"
                    class="html-source"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-medium-emphasis mb-1">
                    {{ t('admConfig.statusEditor.instructions.preview') }}
                  </div>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div class="status-instructions instructions-preview" v-html="html"></div>
                </v-col>
              </v-row>
            </v-window-item>
          </v-window>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close(false)">{{ t('admConfig.statusEditor.cancel') }}</v-btn>
        <v-btn variant="flat" color="primary" @click="commit">{{ t('admConfig.statusEditor.confirm') }}</v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ConditionsEditor from './ConditionsEditor.vue'
import { describeCondition, normalizeConditions } from '../utils/fieldRules.js'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  status: { type: Object, default: null },
  isNew: { type: Boolean, default: false },
  section: { type: String, default: '' },
  allStatuses: { type: Array, default: () => [] },
  aiActions: { type: Array, default: () => [] },
  fields: { type: Array, default: () => [] },
  roles: { type: Array, default: () => [] },
  instructionsHtml: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'commit'])

// Emoji già in uso in cfg.json: scelta rapida per non dover cercare un selettore di sistema
const EMOJI_QUICK_PICK = ['💰', '📊', '⏳', '📜', '🚢', '📝', '💳', '📋', '🔓', '📄', '✅', '💵', '🏭', '🏁', '📥', '🔎', '⚠️']
const GROUPS = ['running', 'factured']
const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/
const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/

// Profili che operano questa fase. Vuoto = fase di tutti, ed e' il default storico.
const statusRoles = ref([])

const activeTab = ref('base')
const errors = ref([])

// Stato scomposto: le parti opzionali si ricompongono al commit, così il JSON resta pulito
const draft = ref(null)
const nextStates = ref([])
const conditionalMode = ref('none')
const conditional = ref({ field: '', value: '', values: [] })
const followUpRules = ref([])
const deadlines = ref([])
const verifications = ref([])
const html = ref('')
const previousKey = ref(null)

// Biblioteca delle azioni IA: lo stato le richiama per nome, non le riconfigura.
const actionFor = (verification) => props.aiActions.find(a => a.id === verification.ref) || null

// Azioni ancora non richiamate da questo stato: una sola volta a testa, altrimenti due pulsanti
// identici scriverebbero sullo stesso storico di esecuzioni.
const addableActionItems = computed(() => {
  const used = new Set(verifications.value.map(v => v.ref))
  return props.aiActions
    .filter(a => !used.has(a.id))
    .map(a => ({
      title: `${a.label} (${a.id})`,
      value: a.id,
      props: {
        subtitle: a.steps?.length
          ? t('admConfig.aiActions.stepCount', { count: a.steps.length })
          : (a.skill || '')
      }
    }))
})

const actionToAdd = ref(null)

const groupItems = computed(() => GROUPS.map(g => ({ title: t(`dashboard.groups.${g}`, g), value: g })))

// Riassunto dei prerequisiti che il riferimento eredita dalla biblioteca, per non doverli andare
// a cercare nell'editor delle azioni prima di decidere se sovrascriverli.
const inheritedConditionsLabel = (verification) => {
  const conditions = normalizeConditions(actionFor(verification)?.conditionalOn)
  if (!conditions.length) return t('admConfig.conditions.inheritedNone')
  const words = {
    yes: t('admConfig.conditions.yes'),
    no: t('admConfig.conditions.no'),
    filled: t('admConfig.conditions.filled'),
    empty: t('admConfig.conditions.emptyValue')
  }
  const text = conditions.map(c => describeCondition(c, fieldLabelOf(c.field), words)).join(' · ')
  return t('admConfig.conditions.inherited', { conditions: text })
}

const fieldLabelOf = (name) => props.fields.find(f => f.name === name)?.label || name

const fieldNameItems = computed(() => props.fields.map(f => ({ title: `${f.label || f.name} (${f.name})`, value: f.name })))
const dateFieldItems = computed(() => props.fields
  .filter(f => f.type === 'date' || f.type === 'datetime')
  .map(f => ({ title: `${f.label || f.name} (${f.name})`, value: f.name })))
const roleItems = computed(() =>
  props.roles.map(r => ({ title: `${r.label || r.id} (${r.id})`, value: r.id })))

const otherStatusItems = computed(() => props.allStatuses
  .filter(s => Number(s.id) !== Number(draft.value?.id))
  .map(s => ({ title: `${s.icon || ''} ${s.descr} (${s.id})`.trim(), value: Number(s.id) })))

const danglingNext = computed(() => {
  const ids = new Set(props.allStatuses.map(s => Number(s.id)))
  return nextStates.value.filter(n => !ids.has(Number(n)))
})

const reachableFrom = computed(() => props.allStatuses.filter(s =>
  Number(s.id) !== Number(draft.value?.id) && (s.nextStates || []).some(n => Number(n) === Number(draft.value?.id))
))

const conditionalModeItems = computed(() => [
  { title: t('admConfig.statusEditor.status.condNone'), value: 'none' },
  { title: t('admConfig.statusEditor.status.condEquals'), value: 'value' },
  { title: t('admConfig.statusEditor.status.condNotEquals'), value: 'notValue' },
  { title: t('admConfig.statusEditor.status.condIn'), value: 'values' }
])

// Valori proposti: le opzioni del campo scelto come condizione, se ne ha
const conditionalValueSuggestions = computed(() => {
  const fieldName = conditional.value.field
  if (!fieldName) return []
  const field = props.fields.find(f => f.name === fieldName && Array.isArray(f.options))
  return (field?.options || []).map(o => String(o.value ?? o))
})

const canonicalKey = computed(() => `${props.section}.${draft.value?.id}`)
const keyIsNonCanonical = computed(() =>
  !!draft.value?.instructionKey && draft.value.instructionKey !== canonicalKey.value
)

const normalizeHex = (value) => {
  if (typeof value !== 'string') return value
  // v-color-picker in modalità hex può restituire #RRGGBBAA: cfg.json accetta solo #RRGGBB
  return value.length === 9 ? value.slice(0, 7) : value
}

const insertSnippet = (snippet) => {
  html.value = `${html.value}${html.value && !html.value.endsWith('\n') ? '\n' : ''}${snippet}\n`
}

const addFollowUpRule = () => followUpRules.value.push({ field: '', value: '', days: 1 })
const addDeadline = () => deadlines.value.push({
  field: '', doneField: '', label: '', offsetDays: null, warnHours: 24, criticalHours: 4, alertOnly: false
})
// Aggiunge il riferimento all'azione scelta: qui si decide solo QUALE azione e se è accesa
const addVerification = () => {
  if (!actionToAdd.value) return
  verifications.value.push({ ref: actionToAdd.value, id: '', enabled: true, ownConditions: false, conditionalOn: [] })
  actionToAdd.value = null
}

const removeVerification = (index) => {
  verifications.value.splice(index, 1)
}

// L'ordine dell'array è l'ordine dei pulsanti nel form
const moveVerification = (index, delta) => {
  const target = index + delta
  if (target < 0 || target >= verifications.value.length) return
  const [moved] = verifications.value.splice(index, 1)
  verifications.value.splice(target, 0, moved)
}

// ---------- Caricamento della bozza ----------

const loadDraft = () => {
  errors.value = []
  activeTab.value = 'base'

  const source = props.status ? JSON.parse(JSON.stringify(props.status)) : null
  if (!source) {
    draft.value = null
    return
  }

  nextStates.value = (source.nextStates || []).map(Number)
  followUpRules.value = (source.followUpRules || []).map(rule => ({
    field: rule.condition?.field || '',
    value: rule.condition?.value ?? '',
    days: rule.days ?? 1
  }))
  deadlines.value = (source.deadlines || []).map(d => ({
    field: d.field || '',
    doneField: d.doneField || '',
    label: d.label || '',
    offsetDays: d.offsetDays ?? null,
    warnHours: d.warnHours ?? 24,
    criticalHours: d.criticalHours ?? 4,
    alertOnly: !!d.alertOnly
  }))
  // Ogni voce è un riferimento alla biblioteca: `ref` è l'azione, `id` l'id runtime del pulsante
  // quando differisce (eredità di un cfg.json precedente), `enabled` lo spegne in questo stato.
  verifications.value = (source.verifications || []).map(v => ({
    ref: v.ref || v.id || '',
    id: v.ref && v.id && v.id !== v.ref ? v.id : '',
    enabled: v.enabled !== false,
    // Condizioni PROPRIE di questo stato: presenti = sostituiscono quelle della biblioteca (anche
    // una lista vuota, che qui vuol dire "in questa fase il pulsante non ha prerequisiti").
    ownConditions: Object.prototype.hasOwnProperty.call(v, 'conditionalOn'),
    conditionalOn: Array.isArray(v.conditionalOn)
      ? v.conditionalOn.map(c => ({ ...c }))
      : (v.conditionalOn ? [{ ...v.conditionalOn }] : [])
  }))

  const cond = source.conditionalOn
  conditional.value = { field: cond?.field || '', value: '', values: [] }
  if (!cond) {
    conditionalMode.value = 'none'
  } else if (Array.isArray(cond.values)) {
    conditionalMode.value = 'values'
    conditional.value.values = cond.values.map(String)
  } else if (cond.notValue !== undefined) {
    conditionalMode.value = 'notValue'
    conditional.value.value = String(cond.notValue)
  } else {
    conditionalMode.value = 'value'
    conditional.value.value = String(cond.value ?? '')
  }

  statusRoles.value = Array.isArray(source.roles) ? [...source.roles] : []
  html.value = props.instructionsHtml || ''
  previousKey.value = source.instructionKey || null

  delete source.nextStates
  delete source.conditionalOn
  delete source.followUpRules
  delete source.deadlines
  delete source.verifications
  draft.value = source
}

watch(() => props.modelValue, (open) => {
  if (!open) return
  loadDraft()
  // La lista dei modelli si rilegge ad ogni apertura: un agent creato nel frattempo (o una tariffa
  // aggiunta) deve comparire senza ricaricare la pagina.
})

// ---------- Commit ----------

const commit = () => {
  const list = []
  const status = { ...draft.value }

  const id = Number(status.id)
  if (!Number.isInteger(id) || id < 0) list.push(t('admConfig.statusEditor.status.errId'))
  if (props.isNew && props.allStatuses.some(s => Number(s.id) === id)) {
    list.push(t('admConfig.statusEditor.status.errIdDuplicate', { id }))
  }
  status.id = id

  status.descr = (status.descr || '').trim()
  if (!status.descr) list.push(t('admConfig.statusEditor.status.errDescr'))
  if (status.descr.length > 60) list.push(t('admConfig.statusEditor.status.errDescrLong'))

  if (status.color && !HEX_PATTERN.test(status.color)) list.push(t('admConfig.statusEditor.status.errColor'))

  if (conditionalMode.value !== 'none' && !conditional.value.field) {
    list.push(t('admConfig.statusEditor.status.errConditionalField'))
  }

  // ---------- Ricomposizione ----------

  status.nextStates = nextStates.value.map(Number)

  if (conditionalMode.value === 'none') {
    delete status.conditionalOn
  } else if (conditionalMode.value === 'values') {
    status.conditionalOn = { field: conditional.value.field, values: conditional.value.values }
  } else if (conditionalMode.value === 'notValue') {
    status.conditionalOn = { field: conditional.value.field, notValue: conditional.value.value }
  } else {
    status.conditionalOn = { field: conditional.value.field, value: conditional.value.value }
  }

  const rules = followUpRules.value
    .filter(rule => rule.field)
    .map(rule => ({ condition: { field: rule.field, value: rule.value }, days: Number(rule.days) || 0 }))
  if (rules.length) status.followUpRules = rules
  else delete status.followUpRules

  const seenDeadlineFields = new Set()
  const cleanDeadlines = []
  deadlines.value.filter(d => d.field).forEach(d => {
    if (!NAME_PATTERN.test(d.field)) list.push(t('admConfig.statusEditor.deadlines.errField', { field: d.field }))
    if (seenDeadlineFields.has(d.field)) list.push(t('admConfig.statusEditor.deadlines.errDuplicate', { field: d.field }))
    seenDeadlineFields.add(d.field)
    if (!d.label) list.push(t('admConfig.statusEditor.deadlines.errLabel', { field: d.field }))
    if (Number(d.criticalHours) > Number(d.warnHours)) {
      list.push(t('admConfig.statusEditor.deadlines.errCriticalOn', { field: d.field }))
    }
    const entry = {
      field: d.field,
      warnHours: Number(d.warnHours) || 24,
      criticalHours: Number(d.criticalHours) || 4,
      label: d.label
    }
    if (d.doneField) entry.doneField = d.doneField
    if (d.offsetDays !== null && d.offsetDays !== '' && d.offsetDays !== undefined) {
      entry.offsetDays = Number(d.offsetDays)
    }
    if (d.alertOnly) entry.alertOnly = true
    cleanDeadlines.push(entry)
  })
  if (cleanDeadlines.length) status.deadlines = cleanDeadlines
  else delete status.deadlines

  const seenVerificationIds = new Set()
  const cleanVerifications = []
  verifications.value.filter(v => v.ref).forEach(v => {
    const runtimeId = (v.id || v.ref).trim()
    if (seenVerificationIds.has(runtimeId)) {
      list.push(t('admConfig.statusEditor.verifications.errIdDuplicate', { id: runtimeId }))
    }
    seenVerificationIds.add(runtimeId)
    if (!actionFor(v)) {
      list.push(t('admConfig.statusEditor.verifications.errMissingAction', { ref: v.ref }))
    }

    const entry = { ref: v.ref }
    // L'id runtime finisce nel file solo se diverso dal nome dell'azione
    if (v.id && v.id !== v.ref) entry.id = v.id
    // Solo lo spegnimento finisce nel file: l'assenza della proprietà significa accesa
    if (v.enabled === false) entry.enabled = false
    // La chiave si scrive solo quando lo stato prende in mano le condizioni: senza, vale quella
    // della biblioteca. Scritta vuota (`[]`) significa invece "qui nessun prerequisito".
    if (v.ownConditions) entry.conditionalOn = v.conditionalOn || []
    cleanVerifications.push(entry)
  })
  if (cleanVerifications.length) status.verifications = cleanVerifications
  else delete status.verifications

  // Profili della fase: assente significa "di tutti", quindi una lista vuota non si scrive.
  // Un profilo che nel frattempo e' stato eliminato dalla biblioteca resterebbe orfano nel file
  // e il Backend rifiuterebbe il salvataggio: meglio dirlo qui, con il nome sotto gli occhi.
  const knownRoles = new Set(props.roles.map(r => r.id))
  const cleanRoles = [...new Set(statusRoles.value.filter(Boolean))]
  cleanRoles.filter(r => !knownRoles.has(r)).forEach(r => {
    list.push(t('admConfig.statusEditor.status.errUnknownRole', { role: r }))
  })
  if (cleanRoles.length) status.roles = cleanRoles
  else delete status.roles

  // Proprietà opzionali vuote rimosse: il file resta come è sempre stato scritto a mano
  if (status.followUpDays === null || status.followUpDays === '' || status.followUpDays === undefined) {
    delete status.followUpDays
  } else {
    status.followUpDays = Number(status.followUpDays)
  }
  ;['isOptional', 'canArchive', 'exclusive'].forEach(key => { if (!status[key]) delete status[key] })
  ;['color', 'icon', 'group', 'followUpCapField', 'followUpCapReleaseField', 'instructionKey'].forEach(key => {
    if (!status[key]) delete status[key]
  })

  if (status.followUpCapField && !NAME_PATTERN.test(status.followUpCapField)) {
    list.push(t('admConfig.statusEditor.status.errCapField'))
  }

  errors.value = list
  if (list.length) {
    activeTab.value = 'base'
    return
  }

  emit('commit', { status, instructionsHtml: html.value, previousKey: previousKey.value })
}

const close = (value) => {
  if (value === false || value === undefined) emit('update:modelValue', false)
}
</script>

<style scoped>
/* Verifica spenta: resta modificabile, ma si vede a colpo d'occhio che non è attiva */
.verification--disabled {
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.02);
}
.color-swatch {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.2);
  cursor: pointer;
  flex-shrink: 0;
}
.html-source :deep(textarea) {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem;
}
.instructions-preview {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 12px;
  min-height: 300px;
  max-height: 420px;
  overflow: auto;
  font-size: 0.85rem;
}
.instructions-preview :deep(ol),
.instructions-preview :deep(ul) {
  padding-left: 20px;
}
.instructions-preview :deep(.tip) {
  color: #1976D2;
  font-style: italic;
}
</style>
